import React, { useState, useEffect, useCallback } from 'react';
import { Envelope, ArrowsClockwise, CheckCircle, XCircle, PaperPlaneTilt } from '@phosphor-icons/react';
import axios from '../../utils/axiosInstance';

const API = `${window.location.origin}/api`;

const KIND_LABELS = {
  salary: { label: 'Wynagrodzenie', color: 'bg-emerald-100 text-emerald-800' },
  terminy_admin: { label: 'Terminy (admin)', color: 'bg-red-100 text-red-800' },
  urlop_admin: { label: 'Urlop (admin)', color: 'bg-sky-100 text-sky-800' },
  generic: { label: 'Inne', color: 'bg-slate-100 text-slate-700' },
};

export default function NotificationsTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [triggering, setTriggering] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? `?kind=${filter}` : '';
      const res = await axios.get(`${API}/salary/notifications-log${params}`);
      setLogs(res.data || []);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const triggerTerminy = async () => {
    if (!window.confirm('Uruchomić sprawdzanie terminów teraz? Email zostanie wysłany na admin.')) return;
    setTriggering(true);
    try {
      const res = await axios.post(`${API}/salary/notify-terminy-now`);
      alert(res.data?.message || 'Sprawdzanie terminów uruchomione');
      await fetchLogs();
    } catch (err) {
      alert(err.response?.data?.detail || 'Błąd');
    } finally {
      setTriggering(false);
    }
  };

  const stats = {
    total: logs.length,
    sent: logs.filter((l) => l.status === 'sent').length,
    failed: logs.filter((l) => l.status === 'failed').length,
  };

  return (
    <div className="space-y-6" data-testid="notifications-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Envelope className="w-7 h-7 text-emerald-600" weight="bold" />
            Historia powiadomień
          </h2>
          <p className="text-slate-600 text-sm mt-1">Wszystkie wysłane wiadomości email</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="trigger-terminy-btn"
            onClick={triggerTerminy}
            disabled={triggering}
            className={`px-4 py-2 rounded-lg font-semibold flex items-center gap-2 ${
              triggering
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700 text-white shadow'
            }`}
          >
            <PaperPlaneTilt className="w-4 h-4" weight="bold" />
            {triggering ? 'Wysyłanie…' : 'Sprawdź terminy teraz'}
          </button>
          <button
            data-testid="refresh-logs-btn"
            onClick={fetchLogs}
            className="px-4 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold flex items-center gap-2"
          >
            <ArrowsClockwise className="w-4 h-4" weight="bold" />
            Odśwież
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <div className="text-sm text-slate-600">Łącznie</div>
          <div className="text-3xl font-bold text-slate-800">{stats.total}</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 shadow-sm">
          <div className="text-sm text-emerald-700">Dostarczone</div>
          <div className="text-3xl font-bold text-emerald-700">{stats.sent}</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 shadow-sm">
          <div className="text-sm text-red-700">Błędy</div>
          <div className="text-3xl font-bold text-red-700">{stats.failed}</div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-semibold text-slate-700">Filtruj:</span>
        {['', 'salary', 'terminy_admin', 'urlop_admin'].map((k) => (
          <button
            key={k || 'all'}
            data-testid={`filter-${k || 'all'}`}
            onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${
              filter === k
                ? 'bg-emerald-600 text-white'
                : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
            }`}
          >
            {k ? KIND_LABELS[k]?.label || k : 'Wszystkie'}
          </button>
        ))}
      </div>

      {/* Log Table */}
      <div className="bg-white rounded-sm shadow-lg border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="text-center py-12 text-slate-500">Ładowanie…</div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16">
            <Envelope className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">Brak wysłanych powiadomień</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="notifications-log-table">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase text-slate-600">Data</th>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase text-slate-600">Typ</th>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase text-slate-600">Odbiorca</th>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase text-slate-600">Temat</th>
                  <th className="text-center py-3 px-4 text-xs font-bold uppercase text-slate-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const kindCfg = KIND_LABELS[log.kind] || KIND_LABELS.generic;
                  const ok = log.status === 'sent';
                  return (
                    <tr
                      key={log.id}
                      className="hover:bg-slate-50 transition-colors"
                      data-testid={`log-row-${log.id}`}
                    >
                      <td className="py-3 px-4 text-sm text-slate-600 font-mono">
                        {(log.sent_at || '').replace('T', ' ').slice(0, 19)}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`text-xs font-semibold px-2 py-1 rounded-md ${kindCfg.color}`}>
                          {kindCfg.label}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-800 font-medium">{log.to}</td>
                      <td className="py-3 px-4 text-sm text-slate-700 max-w-md truncate">
                        {log.subject}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {ok ? (
                          <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold text-sm">
                            <CheckCircle className="w-4 h-4" weight="bold" />
                            Dostarczone
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 text-red-600 font-semibold text-sm"
                            title={log.error || ''}
                          >
                            <XCircle className="w-4 h-4" weight="bold" />
                            Błąd
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
